/**
 * Sidebar — kanonische Verbund-Referenz der Plattform-Shell (UX_KONZEPT §1.2/§6.5).
 *
 * Diese Komponente ist das kopierbare Muster für alle RMNC-Apps
 * (stammdaten/technik/energie): datengesteuerte Navigation über `navItems`,
 * aufklappbare Gruppen (`children`), Rollen-Sichtbarkeit (`requiredRoles`)
 * und App-Akzentfarbe als Prop. Portierungsanleitung: docs/SHELL_PORTIERUNG.md.
 *
 * App-spezifisch beim Kopieren: `navItems`-Liste (+Icons), Rollen-Quelle
 * (hier `useAuthStore`), Branding-Kopf, localStorage-Präfix, `accent`-Prop.
 */
import { useEffect, useState } from "react"
import { Link, useLocation } from "react-router-dom"
import {
  ChevronDown,
  LayoutDashboard,
  Users,
  ClipboardCheck,
  CalendarRange,
  CalendarOff,
  Palmtree,
  GraduationCap,
  HeartPulse,
  Network,
  FileText,
  CalendarDays,
  BookOpen,
  Settings,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { useAuthStore } from "@/store/auth"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

/** Blatt-Eintrag: navigiert direkt zu einer Route. */
export interface NavLeaf {
  title: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  /** Nur sichtbar, wenn der Benutzer mindestens eine dieser Rollen hat.
   *  Frontend-Rollen sind reines Ein-/Ausblenden — durchgesetzt wird serverseitig. */
  requiredRoles?: string[]
}

/** Gruppen-Eintrag: aufklappbar, enthält Blatt-Einträge (eine Ebene tief). */
export interface NavGroup {
  title: string
  icon: React.ComponentType<{ className?: string }>
  children: NavLeaf[]
  /** Gilt zusätzlich zu den requiredRoles der Kinder (Gruppe komplett ausblenden). */
  requiredRoles?: string[]
}

export type NavEntry = NavLeaf | NavGroup

/** Rückwärtskompatibler Alias (frühere flache Struktur). */
export type NavItem = NavLeaf

export function isNavGroup(entry: NavEntry): entry is NavGroup {
  return "children" in entry
}

/**
 * Statischer Navigations-Index.
 *
 * Gruppen-Beispiel „Organisation": Organigramm, Dokumente und Betriebskalender
 * hängen fachlich zusammen und demonstrieren das Muster, das rmnc-technik für
 * Maschinen-/TimeTab-Untertabs 1:1 übernimmt.
 * Rollen-Beispiel „Stammdaten": nur für `admin` sichtbar (Muster für
 * Stammdaten-Finanzen bzw. Energie-GF-Sonderfall).
 */
export const navItems: NavEntry[] = [
  { title: "Dashboard", href: "/", icon: LayoutDashboard },
  { title: "Mitarbeiter", href: "/mitarbeiter", icon: Users },
  { title: "Anwesenheit", href: "/anwesenheit", icon: ClipboardCheck },
  { title: "Schichtplan", href: "/schichtplan", icon: CalendarRange },
  { title: "Abwesenheiten", href: "/abwesenheiten", icon: CalendarOff },
  { title: "Urlaub & Krank", href: "/urlaub", icon: Palmtree },
  { title: "Schulungen", href: "/schulungen", icon: GraduationCap },
  { title: "Vorsorge", href: "/vorsorge", icon: HeartPulse },
  {
    title: "Organisation",
    icon: Network,
    children: [
      { title: "Organigramm", href: "/organisation", icon: Network },
      { title: "Dokumente", href: "/dokumente", icon: FileText },
      { title: "Betriebskalender", href: "/betriebskalender", icon: CalendarDays },
    ],
  },
  { title: "Dokumentation", href: "/dokumentation", icon: BookOpen },
  { title: "Stammdaten", href: "/admin/stammdaten", icon: Settings, requiredRoles: ["admin"] },
]

/** Alle Blatt-Einträge in flacher Reihenfolge (z. B. für Such-Indizes). */
export function flattenNavItems(entries: NavEntry[] = navItems): NavLeaf[] {
  return entries.flatMap((e) => (isNavGroup(e) ? e.children : [e]))
}

/** Sichtbarkeits-Filter: Eintrag ohne requiredRoles ist immer sichtbar. */
export function filterNavByRoles(
  entries: NavEntry[],
  roles: readonly string[] | undefined | null
): NavEntry[] {
  const allowed = (required?: string[]) =>
    !required || required.length === 0 || (roles ?? []).some((r) => required.includes(r))
  return entries.flatMap((entry): NavEntry[] => {
    if (!allowed(entry.requiredRoles)) return []
    if (!isNavGroup(entry)) return [entry]
    const children = entry.children.filter((c) => allowed(c.requiredRoles))
    return children.length > 0 ? [{ ...entry, children }] : []
  })
}

/** localStorage-Schlüssel für den Aufklapp-Zustand der Gruppen (pro App anpassen). */
const GROUPS_STORAGE_KEY = "rmnc-personal.sidebar.groups"

function loadGroupState(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(GROUPS_STORAGE_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : null
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, boolean>
    }
  } catch {
    /* defekter Eintrag → Default */
  }
  return {}
}

interface SidebarProps {
  collapsed?: boolean
  /**
   * App-Akzentfarbe als HSL-Triplet (z. B. "28 80% 52%" für Technik-Orange).
   * Überschreibt `--primary` lokal für die Sidebar (Aktiv-Marker, Monogramm).
   * Ohne Angabe gilt der Theme-Token der App — personal lässt das leer.
   */
  accent?: string
}

export function Sidebar({ collapsed = false, accent }: SidebarProps) {
  const location = useLocation()
  const roles = useAuthStore((s) => s.user?.roles)
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(loadGroupState)

  const visibleItems = filterNavByRoles(navItems, roles)

  function isActive(href: string): boolean {
    if (href === "/") return location.pathname === "/"
    return location.pathname === href || location.pathname.startsWith(href + "/")
  }

  function isGroupActive(group: NavGroup): boolean {
    return group.children.some((c) => isActive(c.href))
  }

  /** Gruppe mit aktiver Route ist immer offen (Auto-Aufklappen bei Navigation). */
  function isGroupOpen(group: NavGroup): boolean {
    if (isGroupActive(group)) return true
    return openGroups[group.title] ?? false
  }

  function toggleGroup(group: NavGroup) {
    setOpenGroups((prev) => ({ ...prev, [group.title]: !isGroupOpen(group) }))
  }

  useEffect(() => {
    try {
      localStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify(openGroups))
    } catch {
      /* Quota/Privacy-Mode: Zustand bleibt dann nur in-memory */
    }
  }, [openGroups])

  function leafClasses(active: boolean, indented = false): string {
    return cn(
      "flex items-center gap-3 border-l-4 border-transparent px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground",
      active && "sidebar-item-active",
      indented && !collapsed && "pl-10",
      collapsed && "justify-center px-0"
    )
  }

  function renderLeaf(item: NavLeaf, indented = false) {
    const Icon = item.icon
    const link = (
      <Link key={item.href} to={item.href} className={leafClasses(isActive(item.href), indented)}>
        <Icon className="h-4 w-4 shrink-0" />
        {!collapsed && <span className="truncate">{item.title}</span>}
      </Link>
    )
    if (!collapsed) return link
    return (
      <Tooltip key={item.href}>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right">{item.title}</TooltipContent>
      </Tooltip>
    )
  }

  function renderGroup(group: NavGroup) {
    const Icon = group.icon
    const active = isGroupActive(group)

    // Eingeklappt: Gruppe als Flyout-Menü (Icons-only-Modus hat keinen Platz zum Aufklappen).
    if (collapsed) {
      return (
        <DropdownMenu key={group.title}>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <button type="button" className={cn(leafClasses(active), "w-full")} aria-label={group.title}>
                  <Icon className="h-4 w-4 shrink-0" />
                </button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="right">{group.title}</TooltipContent>
          </Tooltip>
          <DropdownMenuContent side="right" align="start" className="w-52">
            <DropdownMenuLabel>{group.title}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {group.children.map((child) => {
              const ChildIcon = child.icon
              return (
                <DropdownMenuItem key={child.href} asChild>
                  <Link
                    to={child.href}
                    className={cn(isActive(child.href) && "font-semibold text-primary")}
                  >
                    <ChildIcon className="mr-2 h-4 w-4" />
                    {child.title}
                  </Link>
                </DropdownMenuItem>
              )
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )
    }

    const open = isGroupOpen(group)
    return (
      <div key={group.title}>
        <button
          type="button"
          onClick={() => toggleGroup(group)}
          aria-expanded={open}
          className={cn(
            "flex w-full items-center gap-3 border-l-4 border-transparent px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground",
            active && !open && "sidebar-item-active"
          )}
        >
          <Icon className="h-4 w-4 shrink-0" />
          <span className="flex-1 truncate text-left">{group.title}</span>
          <ChevronDown
            className={cn("h-4 w-4 shrink-0 transition-transform duration-200", !open && "-rotate-90")}
          />
        </button>
        {open && (
          <div className="flex flex-col gap-0.5">
            {group.children.map((child) => renderLeaf(child, true))}
          </div>
        )}
      </div>
    )
  }

  return (
    <aside
      style={accent ? ({ "--primary": accent } as React.CSSProperties) : undefined}
      className={cn(
        "flex h-full flex-col border-r bg-[hsl(var(--sidebar-background))] text-[hsl(var(--sidebar-foreground))] transition-all duration-200",
        collapsed ? "w-16" : "w-60"
      )}
    >
      {/* FabrikOS-Plattformmarke: ausgeklappt Wortmarke, eingeklappt Symbol */}
      <div className={cn("flex h-14 items-center border-b px-4", collapsed && "justify-center px-0")}>
        <Link to="/" aria-label="FabrikOS">
          {collapsed ? (
            <img src={`${import.meta.env.BASE_URL}branding/fabrikos/symbol.jpg`} alt="FabrikOS" className="h-8 w-8 shrink-0" />
          ) : (
            <img src={`${import.meta.env.BASE_URL}branding/fabrikos/logo.svg`} alt="FabrikOS" className="h-6 w-auto" />
          )}
        </Link>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1">
        <nav className="flex flex-col gap-0.5 py-2">
          {visibleItems.map((entry) => (isNavGroup(entry) ? renderGroup(entry) : renderLeaf(entry)))}
        </nav>
      </ScrollArea>
    </aside>
  )
}
